import { v4 as uuidv4 } from "uuid";

import { AnalyticsChartType } from "@formant/core/model/AnalyticsChartType";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IAnalyticsLayout } from "@formant/core/model/IAnalyticsLayout";
import { IAnalyticsModuleParameters } from "@formant/core/model/IAnalyticsModuleParameters";
import { IDashboardModule } from "@formant/core/model/IDashboardModule";
import { ILayoutModule } from "@formant/core/model/ILayoutModule";
import { IStreamColumn } from "@formant/core/model/IStreamColumn";
import { IView } from "@formant/core/model/IView";
import { getColorFromColorSet } from "@formant/core/model/util/getColorFromColorSet";
import {
    ModuleComponentConfigCollection,
    ModulesId,
    NumericComponentConfiguration,
    StreamSource,
} from "@formant/rectangle-sdk";
import { IDashboardView } from "@formant/views-editor/src/types/EditorTypes";

import { metadataStore, observeStore, viewStore } from "../../../../stores";
import { scaleV1RectToCoherenceGrid } from "./coherence-grid";

export const handleAnalyticsMigration = async (
    view: DefinedEntity<IView>,
    deviceId: string | undefined,
) => {
    const layout = view.layout as IAnalyticsLayout;
    const modules = await Promise.all(
        Object.values(layout).map(async moduleInfo =>
            generateAnalyticsModule(moduleInfo, deviceId),
        ),
    );

    const modulesObject = modules
        .filter(module => module !== undefined)
        .reduce(
            (acc, module) => ({
                ...acc,
                [module.graph.id]: module,
            }),
            {},
        );

    return await createV2View(view, deviceId, modulesObject);
};

const createV2View = async (
    view: DefinedEntity<IView>,
    deviceId: string | undefined,
    modules: {
        [moduleId: string]: IDashboardModule;
    },
) => {
    const newView = {
        name: view.name + " - Coherence",
        layout: {
            modules,
            timelineVisible: true,
            timelineType: "aggregate",
            aggregateTimeRange:
                observeStore.analyticsTempStartDate &&
                observeStore.analyticsTempEndDate
                    ? {
                          start: observeStore.analyticsTempStartDate,
                          end: observeStore.analyticsTempEndDate,
                      }
                    : undefined,
        },
        // Fleet-level migrations keep the source view's scoping instead of
        // an empty device filter.
        filter: deviceId ? { deviceIds: [deviceId] } : (view.filter ?? {}),
        groupFilter: view.groupFilter ?? {},
        tags: view.tags,
        layoutType: "dashboard",
        deviceFilter: {},
        index: 0,
        configuration: [],
    } as any as IDashboardView;

    let createdView: DefinedEntity<IView>;
    try {
        createdView = await viewStore.createSimple(newView);
    } catch (error) {
        throw error;
    }
    return createdView;
};

const generateAnalyticsModule = async (
    moduleInfo: ILayoutModule,
    deviceId: string | undefined,
) => {
    if (moduleInfo.configuration?.moduleType !== "analytics") {
        return;
    }
    const parameters = moduleInfo.configuration
        ?.parameters as IAnalyticsModuleParameters;
    const chartType = parameters.configuration?.chartType;
    if (!parameters || !chartType) {
        return;
    }

    const typeToModuleMap: Record<AnalyticsChartType, ModulesId> = {
        line: ModulesId.LINE_CHART,
        bar: ModulesId.BAR_CHART,
        table: ModulesId.TABLE_CHART,
        pie: ModulesId.PIE_CHART,
        summary: ModulesId.READOUT,
        scatter: ModulesId.SCATTER_CHART,
        json: ModulesId.TABLE_CHART,
    };
    if (!typeToModuleMap[chartType]) {
        return;
    }
    const moduleComponentConfig =
        ModuleComponentConfigCollection.autoConfigureModuleComponent(
            typeToModuleMap[chartType],
        );

    const moduleId = uuidv4();
    const con = new ModuleComponentConfigCollection(moduleId);
    con.widgets.push(moduleComponentConfig);
    const nodeGraph = con.buildGraph();
    // Coherence titles modules by graph.name first, so it must carry the v1
    // display name (AnalyticsModuleName), not the internal layout name.
    nodeGraph.name = parameters.AnalyticsModuleName || moduleInfo.name;
    const scaledPosition = scaleV1RectToCoherenceGrid(
        moduleInfo,
        typeToModuleMap[chartType] === ModulesId.LINE_CHART,
    );
    const moduleLayout = {
        name: parameters.AnalyticsModuleName || moduleInfo.name,
        graph: nodeGraph,
        x: scaledPosition.x,
        y: scaledPosition.y,
        width: scaledPosition.width,
        height: scaledPosition.height,
    };

    if (!parameters.query) {
        return moduleLayout;
    }

    const widget = con.widgets[0] as NumericComponentConfiguration;
    widget.analyticsTimeConfiguration.aggregateLevel =
        parameters.query.aggregateLevel ??
        widget.analyticsTimeConfiguration.aggregateLevel;
    widget.analyticsTimeConfiguration.partitionByColumn =
        parameters.configuration?.advancedChartSettings?.partitionBy ??
        widget.analyticsTimeConfiguration.partitionByColumn;
    widget.analyticsTimeConfiguration.xAxisColumn =
        parameters.configuration?.advancedChartSettings?.xAxis ??
        widget.analyticsTimeConfiguration.xAxisColumn;
    widget.analyticsTimeConfiguration.fillDateGaps =
        parameters.query.fillGaps ??
        widget.analyticsTimeConfiguration.fillDateGaps;

    if (parameters.query.type === "advanced") {
        widget.sqlQuery = parameters.query.sqlQuery;
        moduleLayout.graph = con.buildGraph();
        return moduleLayout;
    }

    if (parameters.query.type === "stream") {
        const tableColumns = parameters.query.tableColumns as IStreamColumn[];
        const streams: StreamSource[] = [];
        const end = new Date();
        const start = new Date(end);
        start.setMonth(start.getMonth() - 1);
        await Promise.all(
            tableColumns.map(async column => {
                if (column.streamType === "numeric") {
                    streams.push({
                        name: column.streamName,
                        path: column.streamName,
                        streamName: column.streamName,
                        customUnit: "",
                        streamType: column.streamType,
                        type: "analytics",
                        color: getColorFromColorSet(new Set()),
                    });
                } else if (column.streamType === "numeric set") {
                    const metadataList =
                        await metadataStore.listMetadataWithCurrentValueAsync({
                            start: start.toISOString(),
                            end: end.toISOString(),
                            deviceIds: deviceId ? [deviceId] : [],
                            names: [column.streamName],
                        });
                    const metadata = metadataList[0];
                    if (
                        !metadata?.currentValue ||
                        !Array.isArray(metadata.currentValue)
                    ) {
                        return;
                    }

                    const selectedColors = new Set<string>();
                    metadata.currentValue.forEach(value => {
                        const assignedColor =
                            getColorFromColorSet(selectedColors);
                        selectedColors.add(assignedColor);
                        streams.push({
                            name: metadata.name + "." + value.label,
                            path: value.label,
                            streamName: metadata.name,
                            customUnit: value.unit,
                            streamType: column.streamType,
                            type: "analytics",
                            color: assignedColor,
                        });
                    });
                } else if (column.streamType === "bitset") {
                    const metadataList =
                        await metadataStore.listMetadataWithCurrentValueAsync({
                            start: start.toISOString(),
                            end: end.toISOString(),
                            deviceIds: deviceId ? [deviceId] : [],
                            names: [column.streamName],
                        });
                    const metadata = metadataList[0];
                    const currentValue = metadata?.currentValue as
                        | { keys: string[]; values: string[] }
                        | undefined;
                    if (!currentValue?.keys) {
                        return;
                    }
                    const selectedColors = new Set<string>();
                    currentValue.keys.forEach(key => {
                        const assignedColor =
                            getColorFromColorSet(selectedColors);
                        selectedColors.add(assignedColor);
                        streams.push({
                            name: column.streamName + "." + key,
                            path: key,
                            streamName: column.streamName,
                            customUnit: "",
                            streamType: column.streamType,
                            type: "analytics",
                            color: assignedColor,
                        });
                    });
                } else {
                    streams.push({
                        name: column.streamName,
                        path: column.streamName,
                        streamName: column.streamName,
                        customUnit: "",
                        streamType: column.streamType,
                        type: "analytics",
                        color: "",
                    });
                }
            }),
        );
        widget.streams = streams;
        moduleLayout.graph = con.buildGraph();
        return moduleLayout;
    }

    moduleLayout.graph = con.buildGraph();
    return moduleLayout;
};
